import { ENDPOINTS, getFullUrl } from "./config";
import {
  StaffSignupPayload,
  StaffCompleteDetailsPayload,
  StaffListingParams,
  StaffDetails,
  StaffOption,
  SalaryRevisionPayload,
  SalaryRevisionItem,
  StaffPaginationData,
  StaffListItem,
  StaffDocument,
} from "../../types/staff.types";

const ST = ENDPOINTS.STAFF;

export class ClinicStaffService {
  private static getToken(): string | null {
    return localStorage.getItem("token");
  }

  private static headers(isFormData = false): Record<string, string> {
    const token = this.getToken();
    const h: Record<string, string> = {
      Authorization: `Bearer ${token}`,
    };
    if (!isFormData) {
      h["Content-Type"] = "application/json";
    }
    return h;
  }

  // 1. Staff Signup (Onboarding)
  static async signupStaff(payload: FormData | StaffSignupPayload): Promise<{
    success: boolean;
    message?: string;
    data?: any;
  }> {
    try {
      const isFormData = payload instanceof FormData;
      const url = getFullUrl(ST.SIGNUP);
      const response = await fetch(url, {
        method: "POST",
        headers: this.headers(isFormData),
        body: isFormData ? payload : JSON.stringify(payload),
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Staff signup error:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to signup staff",
      };
    }
  }

  // 2. Update Complete Staff Details
  static async updateStaffDetails(
    staffId: string,
    payload: FormData | StaffCompleteDetailsPayload
  ): Promise<{
    success: boolean;
    message?: string;
    data?: any;
  }> {
    try {
      const isFormData = payload instanceof FormData;
      const endpoint = ST.UPDATE.replace(":staffId", staffId);
      const url = getFullUrl(endpoint);

      const response = await fetch(url, {
        method: "PUT",
        headers: this.headers(isFormData),
        body: isFormData ? payload : JSON.stringify(payload),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Update staff details error:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to update staff details",
      };
    }
  }

  // 3. Mark Staff Active / Update Status
  static async updateStaffStatus(
    staffId: string,
    status: string
  ): Promise<{
    success: boolean;
    message?: string;
    data?: any;
  }> {
    try {
      const url = getFullUrl(ST.UPDATE_STATUS);

      const response = await fetch(url, {
        method: "PUT",
        headers: this.headers(false),
        body: JSON.stringify({
          staffId,
          status,
        }),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Update staff status error:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to update staff status",
      };
    }
  }

  // 4. Staff Listing
  static async getStaffListing(params: StaffListingParams): Promise<{
    success: boolean;
    message?: string;
    data?: StaffListItem[];
    pagination?: StaffPaginationData;
  }> {
    try {
      const url = new URL(getFullUrl(ST.LISTING));
      if (params.page) url.searchParams.append("page", params.page.toString());
      if (params.pageSize) url.searchParams.append("pageSize", params.pageSize.toString());
      if (params.clinicId) url.searchParams.append("clinicId", params.clinicId);
      if (params.department) url.searchParams.append("department", params.department);
      if (params.designation) url.searchParams.append("designation", params.designation);
      if (params.employeeStatus) url.searchParams.append("employeeStatus", params.employeeStatus);
      if (params.employmentType) url.searchParams.append("employmentType", params.employmentType);
      if (params.searchText) url.searchParams.append("searchText", params.searchText);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: this.headers(false),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Get staff listing error:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch staff listing",
        data: [],
      };
    }
  }

  // 5. Staff Full Details
  static async getStaffDetails(staffId: string): Promise<{
    success: boolean;
    message?: string;
    data?: StaffDetails;
  }> {
    try {
      const endpoint = ST.DETAILS.replace(":staffId", staffId);
      const url = getFullUrl(endpoint);

      const response = await fetch(url, {
        method: "GET",
        headers: this.headers(false),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Get staff details error:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch staff details",
      };
    }
  }

  // 6. Staff Options (Dropdown API for reporting manager)
  static async getStaffOptions(clinicId?: string): Promise<{
    success: boolean;
    message?: string;
    data?: StaffOption[];
  }> {
    try {
      const url = new URL(getFullUrl(ST.OPTIONS));
      if (clinicId) url.searchParams.append("clinicId", clinicId);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: this.headers(false),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Get staff options error:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch staff options",
        data: [],
      };
    }
  }

  // 7. Document Upload & Delete
  static async uploadStaffDocument(
    staffId: string,
    formData: FormData
  ): Promise<{
    success: boolean;
    message?: string;
    data?: StaffDocument;
  }> {
    try {
      const endpoint = ST.DOCUMENT_UPLOAD.replace(":staffId", staffId);
      const url = getFullUrl(endpoint);

      const response = await fetch(url, {
        method: "POST",
        headers: this.headers(true),
        body: formData,
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Upload staff document error:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to upload document",
      };
    }
  }

  static async deleteStaffDocument(documentId: string): Promise<{
    success: boolean;
    message?: string;
    data?: any;
  }> {
    try {
      const endpoint = ST.DOCUMENT_DELETE.replace(":documentId", documentId);
      const url = getFullUrl(endpoint);

      const response = await fetch(url, {
        method: "DELETE",
        headers: this.headers(false),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Delete staff document error:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to delete document",
      };
    }
  }

  // 8. Salary Revision Tracking
  static async addSalaryRevision(payload: SalaryRevisionPayload): Promise<{
    success: boolean;
    message?: string;
    data?: any;
  }> {
    try {
      const url = getFullUrl(ST.SALARY_REVISION);
      const response = await fetch(url, {
        method: "POST",
        headers: this.headers(false),
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Add salary revision error:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to add salary revision",
      };
    }
  }

  static async getSalaryRevisions(staffId: string): Promise<{
    success: boolean;
    message?: string;
    data?: SalaryRevisionItem[];
  }> {
    try {
      const endpoint = ST.SALARY_REVISIONS_HISTORY.replace(":staffId", staffId);
      const url = getFullUrl(endpoint);

      const response = await fetch(url, {
        method: "GET",
        headers: this.headers(false),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Get salary revisions error:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch salary revisions",
        data: [],
      };
    }
  }
}
